import { useEffect, useState, useCallback, useRef } from "react";
import { api, authedFileUrl } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, FileText, Trash2, Upload, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function AdminDocuments() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.get("/documents");
        setDocs(r.data.documents);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!file || !title.trim()) {
            toast.error("Title and PDF file are required");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", title);
            await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setOpen(false);
            setTitle(""); setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to upload");
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/documents/${confirmDelete.id}`);
            toast.success("Deleted");
            setConfirmDelete(null);
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-documents-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Files</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Documents</h1>
                    <p className="text-muted-foreground text-sm mt-1">Upload PDFs for parents to preview (no download).</p>
                </div>
                <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="add-document-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Document
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : docs.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {docs.map((d) => (
                        <Card key={d.id} className="rounded-2xl" data-testid={`admin-doc-${d.id}`}>
                            <CardHeader>
                                <div className="flex items-start gap-3">
                                    <div className="h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center shrink-0">
                                        <FileText className="h-6 w-6 text-secondary-foreground" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <CardTitle className="font-heading text-lg truncate">{d.title}</CardTitle>
                                        <CardDescription>Uploaded {new Date(d.uploaded_at).toLocaleDateString()}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="flex flex-wrap gap-2">
                                <Button onClick={() => setPreview(d)} variant="outline" className="rounded-full" data-testid={`preview-admin-doc-${d.id}`}>
                                    <Eye className="h-4 w-4 mr-2" /> Preview
                                </Button>
                                <Button onClick={() => setConfirmDelete(d)} variant="destructive" className="rounded-full" data-testid={`delete-admin-doc-${d.id}`}>
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Upload PDF Document</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Title</Label>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="h-11 rounded-xl" data-testid="doc-title-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>PDF File</Label>
                            <input ref={fileRef} type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="doc-file-input" />
                            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full h-11 rounded-xl">
                                {file ? file.name : "Choose PDF…"}
                            </Button>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="doc-upload-btn">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Upload className="h-4 w-4 mr-2" /> Upload</>)}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
                <DialogContent className="max-w-5xl h-[85vh] p-0 rounded-2xl overflow-hidden flex flex-col">
                    <DialogHeader className="p-4 border-b border-border">
                        <DialogTitle className="font-heading">{preview?.title}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 bg-muted">
                        {preview && (
                            <iframe title={preview.title} src={authedFileUrl(preview.pdf_url)} className="w-full h-full" data-testid="admin-pdf-preview" />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete document?</AlertDialogTitle>
                        <AlertDialogDescription>&quot;{confirmDelete?.title}&quot; will be permanently removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="rounded-full bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
